import { PeriodWidget } from 'components/PeriodWidget';
import cx from 'clsx';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { type ReactElement, type ReactNode, useId } from 'react';
import { useField } from 'react-final-form';
import type { PlanMeta } from '../state';
import * as styles from './ScheduleModeField.css';
import type { ScheduleAnalysis } from './scheduleAnalysis';

export type ScheduleTransferMode = 'skip' | 'exact' | 'align';

type SourceLoadStatus = 'idle' | 'loading' | 'error' | 'success';

type ScheduleOption = {
  value: ScheduleTransferMode;
  label: string;
  description: ReactNode;
  disabled?: boolean;
};

function sourceUnavailableReason(status: SourceLoadStatus): string {
  if (status === 'loading' || status === 'idle') return 'Loading source plan.';
  return 'Source plan could not be loaded.';
}

function exactDescription(
  status: SourceLoadStatus,
  analysis: ScheduleAnalysis | null,
  destination: PlanMeta,
): ReactNode {
  if (!analysis) return sourceUnavailableReason(status);
  if (!analysis.first || !analysis.last)
    return analysis.exactDisabledReason ?? 'Source plan has no schedule to import.';

  const period = <PeriodWidget date_from={analysis.first} date_to={analysis.last} />;
  if (analysis.exactEnabled) return <>Keeps the schedule on {period}.</>;
  if (analysis.last < destination.date_from) {
    return <>Schedule runs {period} — entirely before the destination period.</>;
  }
  if (analysis.first > destination.date_to) {
    return <>Schedule runs {period} — entirely after the destination period.</>;
  }
  if (analysis.first < destination.date_from && analysis.last > destination.date_to) {
    return <>Schedule runs {period} — extends beyond both ends of the destination period.</>;
  }
  if (analysis.first < destination.date_from) {
    return <>Schedule runs {period} — starts before the destination period.</>;
  }
  return <>Schedule runs {period} — ends after the destination period.</>;
}

function alignDescription(status: SourceLoadStatus, analysis: ScheduleAnalysis | null, destination: PlanMeta): string {
  if (!analysis) return sourceUnavailableReason(status);
  if (!analysis.first || !analysis.alignedFirst || !analysis.alignedLast) {
    return analysis.alignDisabledReason ?? 'Source plan has no schedule to import.';
  }

  const offsetDays = differenceInCalendarDays(parseISO(analysis.alignedFirst), parseISO(analysis.first));
  const weeks = offsetDays / 7;
  const shift =
    weeks === 0
      ? 'No date shift is needed; weekdays stay unchanged.'
      : `Shift by ${weeks > 0 ? '+' : ''}${weeks} ${Math.abs(weeks) === 1 ? 'week' : 'weeks'} keeps weekdays.`;
  const alignedStart = format(parseISO(analysis.alignedFirst), 'EEE dd.MM');
  const alignedEnd = format(parseISO(analysis.alignedLast), 'dd.MM');

  if (analysis.alignedLast > destination.date_to) {
    const overrun = differenceInCalendarDays(parseISO(analysis.alignedLast), parseISO(destination.date_to));
    return `${shift} Starts ${alignedStart}, but ends ${alignedEnd} — ${overrun} ${
      overrun === 1 ? 'day' : 'days'
    } past the destination end.`;
  }
  return `${shift} Starts ${alignedStart} and ends ${alignedEnd}.`;
}

function getScheduleOptions(
  status: SourceLoadStatus,
  analysis: ScheduleAnalysis | null,
  destination: PlanMeta,
): ScheduleOption[] {
  return [
    {
      value: 'skip',
      label: "Don't import schedule",
      description: 'Rows come over without positions or placements.',
    },
    {
      value: 'exact',
      label: 'Keep original dates',
      description: exactDescription(status, analysis, destination),
      disabled: !analysis?.exactEnabled,
    },
    {
      value: 'align',
      label: 'Shift to destination',
      description: alignDescription(status, analysis, destination),
      disabled: !analysis?.alignEnabled,
    },
  ];
}

export function ScheduleModeField({
  status,
  analysis,
  destination,
}: {
  status: SourceLoadStatus;
  analysis: ScheduleAnalysis | null;
  destination: PlanMeta;
}): ReactElement {
  const id = useId();
  const { input } = useField<ScheduleTransferMode>('schedule', { subscription: { value: true } });
  const options = getScheduleOptions(status, analysis, destination);

  return (
    <fieldset className={styles.section}>
      <legend className={styles.title}>Schedule</legend>
      <div className={styles.options}>
        {options.map((option) => {
          const optionId = `${id}-${option.value}`;
          const descriptionId = `${optionId}-description`;
          return (
            <label
              key={option.value}
              htmlFor={optionId}
              className={cx(styles.option, {
                [styles.selected]: input.value === option.value,
                [styles.disabled]: option.disabled,
              })}
            >
              <input
                id={optionId}
                type="radio"
                name={input.name}
                value={option.value}
                checked={input.value === option.value}
                disabled={option.disabled}
                aria-describedby={descriptionId}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                onChange={() => input.onChange(option.value)}
                className={styles.radio}
              />
              <span className={styles.body}>
                <span className={styles.header}>
                  <span className={styles.optionTitle}>{option.label}</span>
                  {option.disabled && <span className={styles.unavailable}>Unavailable</span>}
                </span>
                <span id={descriptionId} className={styles.description}>
                  {option.description}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
